import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useOrgRole } from "@/hooks/useOrgRole";
import { useStore } from "@/store";
import type React from "react";
import { Navigate, useLocation } from "react-router-dom";

const WIZARD_PATH = "/setup-wizard";

interface EmailVerificationGuardProps {
  children: React.ReactNode;
}

export default function EmailVerificationGuard({
  children,
}: EmailVerificationGuardProps) {
  const {
    currentUser,
    userData,
    isUserLoading,
    orgId,
    hasCheckedOrg,
    orgFetchError,
    fetchMyOrganization,
    domains,
    hasLoadedDomains,
    domainError,
  } = useStore();
  const { isAdmin } = useOrgRole();
  const location = useLocation();

  // If user is not authenticated, let ProtectedRoute handle it
  if (!currentUser) {
    return <>{children}</>;
  }

  // If user is authenticated but email is not verified, redirect to verification page
  if (!currentUser.emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  // Wait for userData to load before checking wizard status
  if (isUserLoading || !userData) {
    return (
      <div className="flex items-center justify-center h-screen-mobile">
        <Spinner variant="circle" />
      </div>
    );
  }

  // The organization lookup failed (network/server error) -- this is not
  // the same as a confirmed "no org" (404), so don't route into org
  // creation. Offer a retry instead of silently misrouting an existing
  // org member.
  if (orgFetchError && !hasCheckedOrg) {
    return (
      <div className="flex h-screen-mobile flex-col items-center justify-center gap-4">
        <p className="text-sm text-muted-foreground">
          Couldn't check your organization: {orgFetchError}
        </p>
        <Button onClick={() => void fetchMyOrganization()}>Retry</Button>
      </div>
    );
  }

  // Wait for the organization lookup before deciding anything below --
  // domains/pages/everything domain-scoped requires an org, so this must be
  // resolved before the wizard-completion check.
  if (!hasCheckedOrg) {
    return (
      <div className="flex items-center justify-center h-screen-mobile">
        <Spinner variant="circle" />
      </div>
    );
  }

  // No organization yet -- there's no auto-provisioning; the user must
  // create one (or be added to an existing one by its admin) before
  // anything domain-scoped can work.
  if (!orgId) {
    if (location.pathname !== "/setup-organization") {
      return <Navigate to="/setup-organization" replace />;
    }
    return <>{children}</>;
  }

  // Has an org but is lingering on the create-org page (e.g. manual nav
  // after already completing it) -- move them along. Whoever just created an
  // org is its admin with no domains yet, so the wizard rule below keeps them
  // there; anyone else is bounced on to the dashboard by the same rule.
  if (location.pathname === "/setup-organization") {
    return <Navigate to={WIZARD_PATH} replace />;
  }

  const isWizardRoute = location.pathname === WIZARD_PATH;
  // "Add new website" re-enters the wizard deliberately, after setup is long
  // finished. Same param SetupWizard itself reads to skip its own redirects.
  const isReconnect =
    new URLSearchParams(location.search).get("reconnect") === "true";
  const isWizardDone = userData.hasCompletedSetupWizard === true;

  // The wizard asks "is this ORG unconfigured?", but `hasCompletedSetupWizard`
  // only answers "is this USER new?". A member added by email is a fresh
  // signup, so the flag alone dropped every joining member into a 7-step setup
  // for an org that was already set up -- and invited a viewer to add domains
  // and generate personas, which the API 403s.
  //
  // Two conditions, deliberately not the same expression. Entering needs the
  // domain count; leaving must not use it, or an admin who adds their first
  // domain at step 1 would be ejected from the wizard at step 2.
  //
  // `hasLoadedDomains` belongs in the *enter* condition too, not only in the
  // wait below: a zero count means "this org has no domains" only once a
  // snapshot has actually reported one. Without it, every state where the count
  // is merely unknown — a listener that errored, or one torn down by a previous
  // session — reads as a confirmed zero and sends a joining admin into a 7-step
  // setup for an org that is already configured. That redirect is a one-way
  // trip: the eject rule is role-based and will not pull an admin back out once
  // the real domains arrive.
  const needsWizard =
    userData.hasCompletedSetupWizard === false &&
    isAdmin &&
    hasLoadedDomains &&
    domains.length === 0;
  const cannotUseWizard = !isAdmin || isWizardDone;

  // Only fresh admins ever wait here, and only to avoid flashing the dashboard
  // on the way into a wizard they do need. On a listener error, fall through
  // rather than hang -- `needsWizard` is false while the count is unknown, so
  // that lands them on the dashboard with the error surfaced there, which is
  // the recoverable end of a wrong guess.
  if (
    userData.hasCompletedSetupWizard === false &&
    isAdmin &&
    !hasLoadedDomains &&
    !domainError
  ) {
    return (
      <div className="flex items-center justify-center h-screen-mobile">
        <Spinner variant="circle" />
      </div>
    );
  }

  if (needsWizard && !isWizardRoute) {
    return <Navigate to={WIZARD_PATH} replace />;
  }

  if (isWizardRoute && cannotUseWizard && !isReconnect) {
    return <Navigate to="/dashboard" replace />;
  }

  // Authenticated, verified, has an org, and either doesn't need the wizard or
  // is legitimately in it.
  return <>{children}</>;
}
